import { ErrorRounded, OpenInNewRounded, ReplayRounded, TroubleshootRounded } from '@mui/icons-material'
import { Alert, AlertTitle, Box, Button, Card, Chip, IconButton, Menu, MenuItem, Modal, Popover, Skeleton, Stack, Tooltip, Typography } from '@mui/material'
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid'
import { useCallback, useEffect, useState } from 'react'
import FlowApiClient from '../../../backend/sub_api_clients/FlowApiClient'
import { IMultipleFlowsConfigs } from '../../../definitions/NodesExecution'
import { useAppDispatch, useAppSelector } from '../../../store/Hooks'
import { changeRoute } from '../../../store/slices/SessionSlice'
import useInvalidate from '../../hooks/useInvalidate'
import useQuery, { buildQueryKey } from '../../hooks/useQuery'
import RoutingManager from '../../routing/Routing'
import RunModal from './RunModal'

const PAGE_SIZE = 20

const buildColumns = (rows: any[]): GridColDef[] => {
    if (!rows?.length) return []

    const dynamicKeys = Object.keys(rows[0]).filter(k => k !== 'uuid' && k !== 'measure')

    dynamicKeys.sort((a, b) => {
        const keyA = a.match(/\((.*?)\)/)?.[1] ?? ''
        const keyB = b.match(/\((.*?)\)/)?.[1] ?? ''

        const numA = parseInt(keyA.replace(/\D/g, ''), 10)
        const numB = parseInt(keyB.replace(/\D/g, ''), 10)

        return numA - numB
    })

    const columns: GridColDef[] = [
        {
            field: 'measure',
            headerName: '',
            width: 300,
            disableColumnMenu: true
        }
    ]

    for (const key of dynamicKeys) {
        columns.push({
            field: key,
            headerName: key,
            width: 300,
            renderCell: params => params.row[key] ?? ''
        })
    }

    return columns
}

const slots: any = { toolbar: GridToolbar }
const slotProps: any = {
    toolbar: {
        showQuickFilter: true,
        quickFilterProps: {
            variant: 'outlined',
            size: 'small'
        }
    },
    loadingOverlay: {
        variant: 'skeleton',
        noRowsVariant: 'skeleton'
    }
}
const sx = {
    '&.MuiDataGrid-root': {
        padding: 3,
        borderRadius: '12px'
    }, '& .MuiDataGrid-cell:focus': {
        outline: 'none'
    },
    '& .MuiDataGrid-cell:focus-within': {
        outline: 'none'
    }
}

export default function ResultPage() {
    const params = RoutingManager.getParams<{ contractUUIDs: string[], eeUUIDs: string[], config: IMultipleFlowsConfigs }>(useAppSelector(s => s.session.routeParams))
    const runMode: 'full' | 'dry' = params?.config?.generateOutputs ? 'dry' : 'full'
    const [openRunModal, setOpenRunModal] = useState(false)
    const [anchorMenu, setAnchorMenu] = useState<null | HTMLElement>(null)
    const [anchorElPopover, setAnchorElPopover] = useState<HTMLButtonElement | null>(null)
    const [blockUI, setBlockUI] = useState(false)
    const [reRunMode, setReRunMode] = useState<'all' | 'onlyErrors' | ''>('')
    const openModal = Boolean(openRunModal)
    const openMenu = Boolean(anchorMenu)
    const openPopover = Boolean(anchorElPopover)
    const invalidateSettlementResults = useInvalidate({ queryKey: buildQueryKey('getSettlementResults' + '/*') })
    const dispatch = useAppDispatch()

    const { status, response, refetch } = useQuery({
        queryKey: buildQueryKey('settlementResult'),
        queryFn: () => FlowApiClient.getSettlementResult(params?.eeUUIDs),
        cache: false,
        enabled: !!params?.eeUUIDs
    })

    useEffect(() => {
        invalidateSettlementResults()
    }, [])

    const handleCloseModal = useCallback(() => {
        if (blockUI) return
        setOpenRunModal(false)
    }, [blockUI])

    const handleClickPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorElPopover(event.currentTarget)
    }

    const handleClosePopover = () => {
        setAnchorElPopover(null)
    }

    if (status === 'loading' || status === 'stale') {
        return (
            <Stack spacing={3} sx={{ width: '100%', height: '100%', padding: 1 }}>
                <Stack direction='row' alignItems='center' justifyContent='space-between'>
                    <Stack direction='row' alignItems='center' gap={1}>
                        <Skeleton variant='text' width={180} height={32} />
                        <Skeleton variant='circular' width={32} height={32} />
                    </Stack>
                    <Stack direction='row' alignItems='center' gap={1}>
                        <Skeleton variant='circular' width={32} height={32} />
                        <Skeleton variant='rounded' width={100} height={36} />
                    </Stack>
                </Stack>
                <Skeleton variant='rounded' height={67} />
                <Skeleton variant='rounded' sx={{ flex: 1 }} />
            </Stack>
        )
    }

    const rows = response?.result ?? []
    const columns = buildColumns(rows)
    const env = response?.executionEnvironments ?? []
    const errors = response?.errors ?? []

    const failedContractUUIDs = env.filter(env => errors.some(err => err.uuid === env.eeUUID)).map(env => env.contractUUID)

    return (
        <>
            <Stack spacing={3} sx={{ width: '100%', height: '100%', padding: 1 }}>
                <Stack direction='row' alignItems='center' justifyContent='space-between'>
                    <Stack direction='row' alignItems='center' gap={1}>
                        <Typography variant='h5' fontWeight={700}>
                            Settlement Result
                        </Typography>
                        <Chip size='small' color={params?.config?.samplePercentage ? 'dryRun' : 'primary'} label={params?.config?.samplePercentage ? 'Dry-run' : 'Full run'} />
                    </Stack>
                    <Stack direction='row' alignItems='center' gap={1}>
                        <Tooltip title='Go to engine audit'>
                            <IconButton size='small' onClick={e => setAnchorMenu(e.currentTarget)} disabled={env?.length === 0}>
                                <TroubleshootRounded fontSize='small' />
                            </IconButton>
                        </Tooltip>
                        <Menu
                            anchorEl={anchorMenu}
                            open={openMenu}
                            onClose={() => setAnchorMenu(null)}>
                            {env.map((v, i) => (
                                <MenuItem
                                    key={i}
                                    onClick={() => {
                                        setAnchorMenu(null)
                                        dispatch(changeRoute({
                                            route: 'flow',
                                            params: { flowId: v.flowUUID, eeId: v.eeUUID }
                                        }))
                                    }}>
                                    {v?.contractName}
                                </MenuItem>
                            ))}
                        </Menu>
                        <IconButton color='error' onClick={handleClickPopover} disabled={errors.length === 0}>
                            <ErrorRounded />
                        </IconButton>
                        <Button
                            sx={{ alignSelf: 'flex-end' }}
                            variant='contained'
                            startIcon={<ReplayRounded />}
                            onClick={() => {
                                setReRunMode('all')
                                setOpenRunModal(true)
                            }}>Re-run
                        </Button>
                    </Stack>
                </Stack>
                {errors.length > 0 && (
                    <>
                        <Alert severity='error'>
                            <AlertTitle>{errors?.length} Failed Engines</AlertTitle>
                            <Typography variant='body2'>
                                Some engines failed to run successfully.
                                {' '}
                                <strong>Open errors list</strong>
                                {' '}
                                to see which contracts failed.
                            </Typography>
                        </Alert>
                        <Popover
                            slotProps={{ paper: { sx: { padding: 2, width: 400, maxHeight: 300 } } }}
                            open={openPopover}
                            anchorEl={anchorElPopover}
                            onClose={handleClosePopover}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right'
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right'
                            }}>
                            <Stack spacing={2}>
                                <Typography variant='h6' fontWeight={500}>Failed flows</Typography>
                                {errors.map((v, i) => (<ErrorList key={i} item={v} executionEnvironments={response.executionEnvironments} setAnchorElPopover={setAnchorElPopover} />))}
                                <Button
                                    size='small'
                                    variant='text'
                                    startIcon={<ReplayRounded />}
                                    onClick={() => {
                                        setAnchorElPopover(null)
                                        setReRunMode('onlyErrors')
                                        setOpenRunModal(true)
                                    }}>Re-run errors
                                </Button>
                            </Stack>
                        </Popover>
                    </>
                )}
                <Stack flexDirection='row' alignItems='center' gap={1}>
                    <Card elevation={2} sx={{ flex: 1, padding: 2, height: 125 }}>
                        <Stack flex={1} spacing={1}>
                            <Typography variant='subtitle1' fontWeight={700}>Multi-Run Info</Typography>
                            <Stack spacing={0.5}>
                                <Stack flexDirection='row' alignItems='center' gap={2}>
                                    <Typography variant='subtitle2' color={t => t.palette.text.secondary} sx={{ flex: 1 }}>Settlement engine:</Typography>
                                    <Typography variant='subtitle2' color={t => t.palette.text.secondary} sx={{ flex: 1 }}>Settlement instance type:</Typography>
                                    <Typography variant='subtitle2' color={t => t.palette.text.secondary} sx={{ flex: 1 }}>Settlement period:</Typography>
                                </Stack>
                                <Stack flexDirection='row' flex={1} gap={2}>
                                    <Typography variant='body2' sx={{ flex: 1 }}>{params?.config?.settlementEngine?.ancestorName ?? '-'}</Typography>
                                    <Box flex={1}>
                                        <Chip size='small' label={params?.config?.settlementInstance} />
                                    </Box>
                                    <Typography variant='body2' sx={{ flex: 1 }}>{params?.config?.from} - {params?.config?.to}</Typography>
                                </Stack>
                            </Stack>
                        </Stack>
                    </Card>
                    <Card elevation={2} sx={{ flex: 0.6, padding: 2, height: 125 }}>
                        <Stack flex={1} spacing={1}>
                            <Typography variant='subtitle1' fontWeight={700}>Dry-Run Configuration</Typography>

                            {params?.config?.generateOutputs
                                ? (
                                        <Stack spacing={0.5}>
                                            <Stack flexDirection='row' alignItems='center'>
                                                <Typography variant='subtitle2' color={t => t.palette.text.secondary} sx={{ flex: 1 }}>Seed:</Typography>
                                                <Typography variant='subtitle2' color={t => t.palette.text.secondary} sx={{ flex: 1 }}>Sample:</Typography>
                                            </Stack>
                                            <Stack flexDirection='row' flex={1}>
                                                <Typography variant='body2' sx={{ flex: 1 }}>{params?.config?.overrideSeed ?? '-'}</Typography>
                                                <Typography variant='body2' sx={{ flex: 1 }}>{params?.config?.samplePercentage}</Typography>
                                            </Stack>
                                        </Stack>
                                    )
                                : (
                                        <Alert severity='info'>
                                            <Typography variant='body2'>
                                                This run is a full run, no configuration available for dry run.
                                            </Typography>
                                        </Alert>
                                    )}

                        </Stack>
                    </Card>
                </Stack>
                <DataGrid
                    sx={sx}
                    disableRowSelectionOnClick
                    getRowId={row => row.uuid}
                    columns={columns}
                    rows={rows}
                    pageSizeOptions={[PAGE_SIZE]}
                    hideFooter
                    slots={slots}
                    slotProps={slotProps}
                />
            </Stack>
            {openModal && (
                <Modal open={openModal} onClose={handleCloseModal} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                    <Box>
                        <RunModal setOpenModal={setOpenRunModal} setBlockUI={setBlockUI} selectedRows={reRunMode === 'all' ? params?.contractUUIDs : failedContractUUIDs} runMode={runMode} config={params?.config} refetch={refetch} />
                    </Box>
                </Modal>
            )}
        </>
    )
}

interface ErrorListProps {
    item: { uuid: string, reason: string[] }
    executionEnvironments: { name: string, eeUUID: string, flowUUID: string, contractUUID?: string, contractName?: string }[]
    setAnchorElPopover: (anchorElPopover: HTMLButtonElement) => void
}
const ErrorList = ({ item, executionEnvironments, setAnchorElPopover }: ErrorListProps) => {
    const dispatch = useAppDispatch()
    const error = executionEnvironments.find(e => e.eeUUID === item.uuid)
    return (
        <Stack flexDirection='row' justifyContent='space-between' alignItems='center'>
            <Stack flexDirection='row' alignItems='center' gap={1.5}>
                <ErrorRounded color='error' />
                <Stack spacing={0.5}>
                    <Typography variant='body2'>{error?.contractName ?? 'Unknown contract'}</Typography>
                    <Typography variant='body2' color='error'>{item.reason[0]}</Typography>
                </Stack>
            </Stack>
            <IconButton onClick={() => {
                setAnchorElPopover(null)
                dispatch(changeRoute({
                    route: 'flow',
                    params: { flowId: error.flowUUID, eeId: item.uuid }
                }))
            }}>
                <OpenInNewRounded />
            </IconButton>
        </Stack>
    )
}
